import { Component, AfterViewInit } from '@angular/core';
import { GlobalFunctions, CsvDataService } from '@app/Global/GlobalFunctions';
import { ClientDataStore } from '@app/Global/ClientDataStore';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { NotifyService } from '@app/Services/NotifyService';
import { NavigationUrls, UsersControllerMethods } from '@app/Global/EnumManager';
import { ApiService } from '@app/Services/APIService';
import { LIXISubmission } from '@app/Global/Models/ClientModels';
import { ConfirmModal } from '@app/Components/Loan/ConfirmModal/ConfirmModal';
import { orderBy, maxBy } from 'lodash-es';
import { Router } from '@angular/router';

@Component({
    selector: 'LIXISubmissionSummary',
    templateUrl: './LIXISubmissionSummary.html',
    styleUrls: ['./LIXISubmissionSummary.scss'],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: '0' }),
                animate('0.1s ease-out', style({ opacity: '1' })),
            ]),
        ]),
    ]
})

export class LIXISubmissionSummary implements AfterViewInit {

    // All submissions
    public AllSubmissions: LIXISubmission[] = [];

    // Filtered submissions (for search)
    public FilteredSubmissions: LIXISubmission[] = [];

    // Current page
    public CurrentPage: number;

    //Used by paginator for config, size of the pages, etc.
    public Paging = {
        MaxSize: 10,
        PreviousLabel: "",
        NextLabel: ""
    }

    // Loading indicator
    public IsGenerateInProgress: boolean = false;

    // Lender GUIDs (for API request), lets default to ALL
    public LenderGUIDs = "{ALL}";

    // Text display limit for errors and warnings
    public TextDisplayLimit: number = 18;

    // Search text
    public SearchText: string = "";

    // Initial Load
    public InitialPageLoad = true;

    // Header columns for sorting    
    public HeaderColumns = [
        { Name: 'OriginatorRef', DisplayName: 'Originator Reference Number', DisplayProperty: 'OriginatorRef', SortKey: 0, SortDirection: 1 },
        { Name: 'Lender', DisplayName: 'Lender', DisplayProperty: 'Lender', SortKey: 0, SortDirection: 1 },
        { Name: 'LodgedDate', DisplayName: 'Lodged Date', DisplayProperty: 'LodgedDate', SortKey: 0, SortDirection: 1 },
        { Name: 'LoanNumbers', DisplayName: 'Loan Numbers', DisplayProperty: 'LoanNumbers', SortKey: 0, SortDirection: 1 },
        { Name: 'PrincipalBorrower', DisplayName: 'Principal Borrower', DisplayProperty: 'PrincipalBorrower', SortKey: 0, SortDirection: 1 },
        { Name: 'Status', DisplayName: 'Status', DisplayProperty: 'Status', SortKey: 0, SortDirection: 1 },
        { Name: 'Errors', DisplayName: 'Errors', DisplayProperty: 'Errors', SortKey: 0, SortDirection: 1 },
        { Name: 'Warnings', DisplayName: 'Warnings', DisplayProperty: 'Warnings', SortKey: 0, SortDirection: 1 }
    ];

    constructor(
        public globalFunctions: GlobalFunctions,
        private clientDataStore: ClientDataStore,
        private dialog: MatDialog,
        private notifyService: NotifyService,
        private apiService: ApiService,
        private csvDataService: CsvDataService,
        private router: Router
    ) { }

    //This will be called after the view has been loaded
    ngAfterViewInit() {

        //Run it on initial load only
        if (this.InitialPageLoad) {

            //We still need a short delay even this method is added on ngAfterViewInit
            this.globalFunctions.delay(50).then(() => {

                // Invoke an API endpoint to get the recent LIXI submission details
                this.LIXISubmissions_Refresh();

                //Set class variable initialPageLoad to false
                this.InitialPageLoad = false;
            });
        }
    }

    // Refresh submissions data
    public LIXISubmissions_Refresh(): void {

        // Reset the search filter
        this.SearchText = "";

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        // Reset arrays
        this.AllSubmissions = [];
        this.FilteredSubmissions = [];

        //Construct API Request
        const apiRequest = { LenderGUIDs: this.LenderGUIDs };

        this.apiService.APIData_Post(this.apiService.Endpoints.UsersController, UsersControllerMethods[UsersControllerMethods.GetRecentLIXISubmissions], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    return;
                }
                else {
                    //Deserialize it into an class that we can understand
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    if (!this.globalFunctions.isEmpty(response) && !this.globalFunctions.isEmpty(response.LIXISubmissions))
                        response.LIXISubmissions.forEach(lixiSubmission => {
                            this.AllSubmissions.push({
                                ID: lixiSubmission.ApplicationID,
                                OriginatorRef: lixiSubmission.LenderReference,
                                LoanNumbers: lixiSubmission.LoanNumbers,
                                CachedAccounts: lixiSubmission.CachedAccounts,
                                Lender: lixiSubmission.LenderName,
                                LenderInternalName: lixiSubmission.ThreeLetterCode,
                                PrincipalBorrower: this.globalFunctions.HTMLUnescape(lixiSubmission.PrincipalBorrower),
                                LodgedDate: this.globalFunctions.isEmpty(lixiSubmission.LodgedDate) ? "" : this.globalFunctions.getCustomDateFormat(lixiSubmission.LodgedDate, "longdate", "custom", "YYYY-MM-DD hh:mm:ss"),
                                Status: lixiSubmission.SubmissionStatus,
                                Errors: this.globalFunctions.HTMLUnescape(lixiSubmission.ErrorMessages),
                                Warnings: this.globalFunctions.HTMLUnescape(lixiSubmission.WarningMessages),
                                DisableBorrowerToolTip: (this.globalFunctions.isEmpty(lixiSubmission.PrincipalBorrower) === true || lixiSubmission.PrincipalBorrower.length <= this.TextDisplayLimit),
                                DisableErrorsToolTip: (this.globalFunctions.isEmpty(lixiSubmission.ErrorMessages) === true || lixiSubmission.ErrorMessages.length <= this.TextDisplayLimit),
                                DisableWarningsToolTip: (this.globalFunctions.isEmpty(lixiSubmission.WarningMessages) === true || lixiSubmission.WarningMessages.length <= this.TextDisplayLimit)
                            });
                        });

                    //Set the FilteredSubmissions same as all submissions
                    this.FilteredSubmissions = [...this.AllSubmissions];

                    // Apply any existing sorting if there are active sort columns
                    if (this.HeaderColumns.some(col => col.SortKey > 0)) {
                        this.CurrentSorting_Apply();
                    }
                }

                //Turn off the fullscreen loading
                this.IsGenerateInProgress = false;
                this.clientDataStore.SetShowFullscreenLoading(false);

                // Reset to first page after refresh
                this.Page_OnChange(0, false);
            });
    }

    // Retrieve LIXI Document
    public LIXIDocument_Get(submission: LIXISubmission): void {

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Construct API Request
        const apiRequest = { LenderGUIDs: this.LenderGUIDs, ApplicationID: submission.ID };

        this.apiService.APIData_Post(this.apiService.Endpoints.UsersController, UsersControllerMethods[UsersControllerMethods.GetLIXIDocument], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    return;
                }
                else {
                    //Deserialize it into an class that we can understand
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    // Check if we have a valid response
                    if (!this.globalFunctions.isEmpty(response) && !this.globalFunctions.isEmpty(response.LIXIDocument)) {
                        // Process the LIXI Document response

                        if (!this.globalFunctions.isEmpty(response.LIXIDocument.LIXIDocument)) {
                            const xmlString = response.LIXIDocument.LIXIDocument;

                            // Format the XML for better readability
                            const formattedXml = this.globalFunctions.XMLDocument_Format(xmlString);

                            // Create styled HTML for the XML display
                            const styledXmlHtml = `
                                        <div class="xml-container text-dark" style="max-height: 70vh; overflow-y: auto; font-family: monospace; white-space: pre; background-color:rgb(249, 246, 246); padding: 15px; border-radius: 5px;">${this.globalFunctions.HTML_Escape(formattedXml)}
                                        </div>
                                    `;

                            // Show the message in a dialog box
                            const dialogRef = this.globalFunctions.FeatureModal_Launch(ConfirmModal, this.globalFunctions.GetConfirmModalConfig('60%'), this.dialog, "Confirm Modal", null, false, false);

                            // Use html content so that we can style it
                            dialogRef.DialogRef.componentInstance.htmlContent = styledXmlHtml;

                            // Hide the No Button
                            dialogRef.DialogRef.componentInstance.ShowNoButton = false;

                            // Change the Yes button text to "OK"
                            dialogRef.DialogRef.componentInstance.YesButtonText = "Ok";

                            // Show drag header bar
                            dialogRef.DialogRef.componentInstance.ShowModalHeader = true;

                            // Toast message
                            this.notifyService.Success_Show("LIXI Document XML retrieved successfully", "LIXI Submission");
                        }
                    }
                }

                //Turn off the fullscreen loading
                this.IsGenerateInProgress = false;
                this.clientDataStore.SetShowFullscreenLoading(false);
            });
    }

    // Clear filter
    public Filter_Clear(): void {

        this.SearchText = "";
        this.FilteredSubmissions = [...this.AllSubmissions];

        // Apply any existing sorting if there are active sort columns
        if (this.HeaderColumns.some(col => col.SortKey > 0)) {
            this.CurrentSorting_Apply();
        }

        // Reset to first page when clearing filter
        this.Page_OnChange(0, false);
    }

    // Handle page change event
    public Page_OnChange(page: number, doScroll = true): void {
        this.CurrentPage = page;

        // Don't scroll to top, bad UI experience. scroll to bottom instead
        if (doScroll) {
            this.globalFunctions.delay(50).then(() => {
                window.scrollTo(0, document.body.scrollHeight);
            });
        }
    }

    // Filter submissions based on search text
    public LIXISubmissions_Filter(event: Event): void {
        const searchText = (event.target as HTMLInputElement).value.toLowerCase();

        if (searchText === "") {
            this.FilteredSubmissions = [...this.AllSubmissions];
            // Reset to first page when clearing filter via the search box
            this.Page_OnChange(0, false);
            return;
        } this.FilteredSubmissions = this.AllSubmissions.filter(submission =>
            (submission.OriginatorRef && submission.OriginatorRef.toLowerCase().includes(searchText)) ||
            (submission.Lender && submission.Lender.toLowerCase().includes(searchText)) ||
            (submission.LenderInternalName && submission.LenderInternalName.toLowerCase().includes(searchText)) ||
            (submission.LoanNumbers && submission.LoanNumbers.toLowerCase().includes(searchText)) ||
            (submission.PrincipalBorrower && submission.PrincipalBorrower.toLowerCase().includes(searchText)) ||
            (submission.Status && submission.Status.toLowerCase().includes(searchText)) ||
            (submission.Errors && submission.Errors.toLowerCase().includes(searchText)) ||
            (submission.Warnings && submission.Warnings.toLowerCase().includes(searchText)) ||
            (submission.LodgedDate && submission.LodgedDate.toLowerCase().includes(searchText))
        );

        // Apply any existing sorting if there are active sort columns
        if (this.HeaderColumns.some(col => col.SortKey > 0)) {
            this.CurrentSorting_Apply();
        }

        // Reset to first page when filtering
        this.Page_OnChange(0, false);
    }

    // Downloads the LIXI submissions data to a CSV file
    public LIXISubmissions_DownloadCSV(): void {

        if (this.FilteredSubmissions.length === 0) {
            this.notifyService.Warning_Show("No data to download", "Download Failed");
            return;
        } const csvArray = [];        // Format data for CSV export (create clean objects without functions/methods)
        this.FilteredSubmissions.forEach(submission => {
            csvArray.push({
                'Originator Reference': this.globalFunctions.HTMLUnescape(submission.OriginatorRef),
                'Lender': submission.Lender,
                'Lodged Date': submission.LodgedDate,
                'Loan Numbers': submission.LoanNumbers || '',
                'Principal Borrower': submission.PrincipalBorrower || '',
                'Status': this.globalFunctions.HTMLUnescape(submission.Status),
                'Errors': this.globalFunctions.HTMLUnescape(submission.Errors),
                'Warnings': this.globalFunctions.HTMLUnescape(submission.Warnings)
            });
        });

        // Call the service to produce and download the csv file
        this.csvDataService.exportToCsv("LIXISubmissions", csvArray);
    }

    //Get CSS for highlighting text based on the class properties
    public StatusCSS_Get(status: string): string {

        //CSS to highlight the text
        const cssStr = "glb_highlightLabelSM glb_highlightLabelPadding";

        if (!this.globalFunctions.isEmpty(status) && status.trim().toLowerCase() === "received") {
            return cssStr + " glb_highlightLabelReceived";
        }
        else if (!this.globalFunctions.isEmpty(status) && status.trim().toLowerCase() === "received with warnings") {
            return cssStr + " glb_highlightLabelReceivedWithWarnings";
        }
        else if (!this.globalFunctions.isEmpty(status) && (status.trim().toLowerCase() === "errors" || status.trim().toLowerCase() === "invalid")) {
            return cssStr + " glb_highlightLabelError";
        }

        //No need to highlight, return empty string
        return "";
    }    // Check if a loan number has a matching cached account
    public CachedAccount_Has(submission: LIXISubmission, accountNumber: string): boolean {
        // If there are no cached accounts, return false
        if (this.globalFunctions.isEmpty(submission.CachedAccounts)) {
            return false;
        }

        // Check if there's a cached account with the given account ID
        return submission.CachedAccounts.some(acc => acc.AccountID === accountNumber);
    }
    // Handle loan number click
    public LoanNumber_Click(submission: LIXISubmission, accountNumber: string): void {
        // Find the matching cached account for the clicked account number 
        const account = submission.CachedAccounts.find(acc => acc.AccountID === accountNumber);

        if (this.globalFunctions.isEmpty(account)) {
            this.notifyService.Warning_Show("No account found for the selected loan number.", "Account Not Found");
            return;
        }

        // Set the selected loan, but we want to use the parsed copy where the highlights are removed
        this.clientDataStore.SetSelectedAccount(account);

        // Now we are ready to route to the account (LoanIndex) page
        // Go to the account page
        const targetURL = [NavigationUrls.Account.replace(':AccountID', account.AccountID)];

        // Tell router to reload the component, even if the URL is the same
        this.router.navigate(targetURL, { onSameUrlNavigation: 'reload' });
    }


    // For re-sorting the list of submissions when a header is clicked
    public Click_Sort(itemKey): void {

        // Find the target header
        const headerColumn = this.HeaderColumns.filter(x => x.Name === itemKey);        // If exists
        if (!this.globalFunctions.isEmpty(headerColumn)) {
            // Is this header already a sorting column
            const sortKey = headerColumn[0].SortKey;

            if (sortKey === 0) {
                // Not a sorting column yet, so it is a first click. Get the current max sorting key
                const maxSortKey = maxBy(this.HeaderColumns, 'SortKey');
                // Set the sorting key to the max number + 1
                const currentMaxSortKey = maxSortKey && typeof maxSortKey.SortKey === 'number' ? maxSortKey.SortKey : 0;
                headerColumn[0].SortKey = currentMaxSortKey + 1;

                // Flip the sorting direction
                headerColumn[0].SortDirection = headerColumn[0].SortDirection * -1;
            }
            // Already a sorting column, and if it's a third click, remove it from the sorting columns
            else if (headerColumn[0].SortDirection === 1) {
                // Reset the sorting data
                headerColumn[0].SortKey = 0;
                headerColumn[0].SortDirection = 1;
            }
            else {
                // Otherwise, just flip the direction, it is a second click
                headerColumn[0].SortDirection = headerColumn[0].SortDirection * -1;
            }
        }

        // Deep clone the header columns for sorting so that it doesn't affect the column display order
        let headerColumnsSort = JSON.parse(JSON.stringify(this.HeaderColumns));

        // Sort the header columns by the sort key using lodash
        headerColumnsSort = orderBy(headerColumnsSort, ["SortKey"]);

        // Arrays to store the sorting columns and their corresponding sorting directions
        const sortingColumns = [];
        const sortingDirections = [];

        // Loop through each header columns
        headerColumnsSort.forEach(element => {

            // See if it is a sorting key
            if (element.SortKey > 0) {

                // Default the sorting to ascending
                let sortingDirection = 'asc';

                // Store the sorting column - use the actual property name
                sortingColumns.push(element.Name);

                // Flip the sorting direction as per the header data
                if (element.SortDirection === -1) {
                    sortingDirection = 'desc';
                }

                // Store sorting direction
                sortingDirections.push(sortingDirection);
            }
        });

        // Sort the submissions data using lodash with case-insensitive iteratees
        this.FilteredSubmissions = orderBy(
            this.FilteredSubmissions,
            sortingColumns.map(colName => item => {

                // Get value from the item using the property name
                const value = item[colName];

                // Convert to lowercase string if value exists for case-insensitive comparison
                return typeof value === 'string' ? value.toLowerCase() : value;
            }),
            sortingDirections);
    }

    // Apply current sorting without changing sort order/columns
    private CurrentSorting_Apply(): void {

        // Deep clone the header columns for sorting
        let headerColumnsSort = JSON.parse(JSON.stringify(this.HeaderColumns));

        // Sort the header columns by the sort key
        headerColumnsSort = orderBy(headerColumnsSort, ["SortKey"]);

        // Arrays for sorting columns and directions
        const sortingColumns = [];
        const sortingDirections = [];        // Process sorting columns
        headerColumnsSort.forEach(element => {
            if (element.SortKey > 0) {

                // Store the sorting column - use the actual property name
                sortingColumns.push(element.Name);

                // Set direction
                const sortingDirection = element.SortDirection === -1 ? 'desc' : 'asc';
                sortingDirections.push(sortingDirection);
            }
        });

        // Apply sorting if we have columns to sort by
        if (sortingColumns.length > 0) {
            this.FilteredSubmissions = orderBy(
                this.FilteredSubmissions,
                sortingColumns.map(colName => item => {

                    // Get value from the item using the property name
                    const value = item[colName];

                    // Convert to lowercase string if value exists for case-insensitive comparison
                    return typeof value === 'string' ? value.toLowerCase() : value;
                }),
                sortingDirections);
        }
    }

    // View submission details
    public SubmissionDetails_View(submission: LIXISubmission, messageType: string = ''): void {

        //Variables to construct the messages
        let messageDetails = "";
        let displayMessageHtml = "";
        let messageTypeDetail = "";

        // If received without any warnings, nothing to display, lets return from here
        if (submission.Status === "Received" || messageType === "")
            return;

        // Get the message details based on message type
        if (messageType === 'Error') {
            messageDetails = submission.Errors;
            messageTypeDetail = "error(s)";
        }
        else if (messageType === 'Warning') {
            messageDetails = submission.Warnings;
            messageTypeDetail = "warning(s)";
        }

        // To split and store the messages for better display
        let displayMessages = [];

        // If not empty
        if (this.globalFunctions.isEmpty(messageDetails) === false) {

            // Split the messaage bases on semicolon
            displayMessages = messageDetails.split(";");

            // If we got any messages, lets construct a proper htmlmessage
            if (displayMessages.length > 0) {

                // HTML body starts here
                displayMessageHtml = "<div>"
                    + "<p>List of " + messageTypeDetail + ": </p>"
                    + "<ul class=\"glb_customFlexRow align-items-center\" style=\"margin-left: 1em; text-align: left;\">";

                // Loop through the messages and add to the list
                displayMessages.forEach(message => {
                    displayMessageHtml += "<li class=\"col-12\">" + message + "</li>";
                });

                // Lets end the HTML tag
                displayMessageHtml += "</ul>"
                    + "</div>";
            }
        }

        // Show the message in a dialog box
        const dialogRef = this.globalFunctions.FeatureModal_Launch(ConfirmModal, this.globalFunctions.GetConfirmModalConfig(), this.dialog, "Confirm Modal", null, false, false);

        // Use html content so that we can style it
        dialogRef.DialogRef.componentInstance.htmlContent = displayMessageHtml;

        // Hide the No Button
        dialogRef.DialogRef.componentInstance.ShowNoButton = false;

        // Change the Yes button text to "OK"
        dialogRef.DialogRef.componentInstance.YesButtonText = "Ok";

        // Show drag header bar
        dialogRef.DialogRef.componentInstance.ShowModalHeader = true;

        // Toast message
        this.notifyService.Success_Show("Submission details retrieved successfully", "LIXI Submission");
    }
}
